import type { Transition } from 'framer-motion'
import {
  SIDEPANEL_CLOSE_DURATION_S,
  SIDEPANEL_CLOSE_EASE,
  SIDEPANEL_OPEN_DURATION_S,
  SIDEPANEL_OPEN_EASE,
} from '@shared/sidepanel/motion'

// Panel slide spring — the peek transition, the re-dock entrance, and
// swipe-dismiss, i.e. everywhere velocity handoff from a gesture matters.
export const SIDEPANEL_SPRING: Transition = { type: 'spring', duration: 0.2, bounce: 0 }

// The open and close, which the native blur behind the panel has to match
// frame for frame — so they're tweens on a curve both platforms can express
// exactly. See `@shared/sidepanel/motion`.
export const SIDEPANEL_OPEN_TWEEN: Transition = {
  type: 'tween',
  duration: SIDEPANEL_OPEN_DURATION_S,
  ease: [...SIDEPANEL_OPEN_EASE],
}

export const SIDEPANEL_CLOSE_TWEEN: Transition = {
  type: 'tween',
  duration: SIDEPANEL_CLOSE_DURATION_S,
  ease: [...SIDEPANEL_CLOSE_EASE],
}

// Snap-back after a cancelled swipe (panel or view). Slightly quicker than
// SIDEPANEL_SPRING since the travel distance is short.
export const SIDEPANEL_SNAP_BACK_SPRING: Transition = { type: 'spring', duration: 0.15, bounce: 0 }

/**
 * The panel card's 1px border, as a number — a View is positioned *inside* it
 * rather than over it, so the border stays unbroken where a View covers Home.
 */
export const SIDEPANEL_SURFACE_BORDER_PX = 1

/**
 * The gutter between the panel card's edge and anything floating inside it.
 * What an open View tops out at (`ViewRouter`) and what the Notepad's full
 * height stops short of, so the two agree by construction.
 */
export const SIDEPANEL_GUTTER_PX = 12

/**
 * Paint order for the panel's overlapping layers, all of which share the
 * panel root's stacking context (`isolate`).
 *
 * They're listed together because the order between them is the contract: a
 * View's bottom sheet has to slide *over* the floating bar (the busy pill and
 * the action's controls belong to the view it covers, so they can't float on
 * top of it) and *under* the chat input (the sheet's own composer). That's
 * impossible while the bar and the input share one z-indexed footer — which is
 * why `SidepanelFooter` deliberately doesn't have a z-index of its own and its
 * children take these values instead.
 *
 * `bottomGlow` sits above the view (the router composites with
 * `will-change: transform` and can hide a same-z sibling) and below the
 * floating bar and composer, so the wash originates behind the chat input.
 *
 * The resize handles sit above all of it at z-40/z-50 — grabbing the panel's
 * edge works no matter what's open.
 */
export const SIDEPANEL_LAYER_Z = {
  /** The view router: an open drill-in View, over Home. */
  view: 6,
  /** State tint from behind the composer, washing up over the open View. */
  bottomGlow: 7,
  /** Floating controls above the chat input, over the open View. */
  floatingBar: 8,
  /** A View's bottom sheet (the Action View's chat drawer), over that bar. */
  viewSheet: 20,
  /** The chat input, over everything — it stays usable with a sheet open. */
  chatInput: 30,
} as const

export const STAGED_ACTION_EDIT_OPTIONS_QUERY_KEY = 'staged-action-edit-options'

export const AUTO_CLEAR_OPTIONS = [
  { label: '1 day', value: '24h', triggerLabel: '1 day' },
  { label: '3 days', value: '72h', triggerLabel: '3 days' },
  { label: '7 days', value: '1week', triggerLabel: '7 days' },
  { label: 'Never', value: 'never', triggerLabel: 'Off' },
] as const

export const AUTO_CLEAR_INTERVAL_MS: Record<string, number> = {
  '24h': 24 * 60 * 60 * 1000,
  '72h': 72 * 60 * 60 * 1000,
  '1week': 7 * 24 * 60 * 60 * 1000,
}
